import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';
import type {
  BMBrokerDetail,
  BMBrokerList,
  BMChainSnapshot,
  BMOperatorCrossBroker,
} from '../types/brokerMonitor';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

function requireTenantPrefix(): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return tenantApiPrefix(tid);
}

async function readJson<T>(response: Response, fallbackMessage: string): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: string }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : fallbackMessage);
  }
  return response.json();
}

export async function fetchBrokerMonitorChain(signal?: AbortSignal): Promise<BMChainSnapshot> {
  const response = await fetch(buildApiUrl(`${requireTenantPrefix()}/broker-monitor/chain`), {
    headers: getAuthHeaders(),
    signal,
  });
  return readJson(response, 'Failed to load Broker Activity Monitor chain');
}

export async function fetchBrokerMonitorBrokers(signal?: AbortSignal): Promise<BMBrokerList> {
  const response = await fetch(buildApiUrl(`${requireTenantPrefix()}/broker-monitor/brokers`), {
    headers: getAuthHeaders(),
    signal,
  });
  return readJson(response, 'Failed to load broker list');
}

export async function fetchBrokerMonitorBrokerDetail(
  brokerId: string,
  signal?: AbortSignal,
): Promise<BMBrokerDetail> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/broker-monitor/brokers/${encodeURIComponent(brokerId)}`),
    { headers: getAuthHeaders(), signal },
  );
  return readJson(response, `Failed to load broker ${brokerId}`);
}

export async function fetchBrokerMonitorOperatorCrossBroker(
  operatorId: string,
  signal?: AbortSignal,
): Promise<BMOperatorCrossBroker> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/broker-monitor/operators/${encodeURIComponent(operatorId)}/cross-broker`),
    { headers: getAuthHeaders(), signal },
  );
  return readJson(response, `Failed to load operator ${operatorId} cross-broker pivot`);
}
